import { useTheme } from '@mui/material'
import Button from '@mui/material/Button'
import Link from '@mui/material/Link'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { lighten } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import { closeSnackbar, enqueueSnackbar } from 'notistack'
import React from 'react'
import { generatePath, useParams } from 'react-router-dom'

import { useLocationContext } from '@providers/location'
import { useSessionHandler } from '@providers/session/use_session_handler'
import { i18WithDefault as t } from '@shared/locale'
import { routes } from 'app/routes_definitions'
import { useRoutes } from 'app/use_routes'
import { SignInTooltip } from 'components-ui/src/signin_tooltip/signin_tooltip'
import { AddressLocation, Phone, Website } from 'components/address'
import { useUserStatus } from 'hooks/use_userstatus'
import TagManager from 'react-gtm-module'
import { AccountButton } from './account_button'
import { HeaderSkeleton } from './header_skeleton'

export const SiteHeader = () => {
  const { isLoading: isLoadingLocation, location } = useLocationContext()
  const { accountName: locationName = '' } = useParams()
  const { navigateRoute } = useRoutes()
  const { isLoggedIn } = useUserStatus()
  const [loginTooltipOpen, setLoginTooltipOpen] = React.useState(false)

  useSessionHandler()

  const theme = useTheme()
  const softPrimary = lighten(theme.palette.primary.main, 0.15)
  const isBelowMD = useMediaQuery(theme.breakpoints.down('md'))

  if (isLoadingLocation) {
    return <HeaderSkeleton />
  }

  const contrastColor = theme.palette.getContrastText(theme.palette.primary.main)

  const handleLoginTooltipOpen = () => {
    setLoginTooltipOpen(true)
    TagManager.dataLayer({
      dataLayer: {
        event: 'LOGIN_START_HEADER',
      },
    })
  }

  const handleLoginTooltipClose = () => {
    setLoginTooltipOpen(false)
  }

  const handleLoginTooltipError = (error: Error) => {
    if (error.message === 'User does not exist.') {
      enqueueSnackbar(t('global.createAccountAlert'), {
        action: (key) => (
          <Button
            color="primary"
            size="large"
            onClick={() => {
              navigateRoute('signIn', { accountName: locationName })
              closeSnackbar(key)
            }}
          >
            {t('global.createAccount')}
          </Button>
        ),
        style: {
          backgroundColor: theme.palette.common.white,
          color: theme.palette.common.black,
        },
      })
      return
    }
  }

  return (
    <Stack
      direction="row"
      sx={{
        px: 2,
        py: 1,
        background: `linear-gradient(180deg, ${theme.palette.primary.main} 25%, ${softPrimary} 100%)`,
        borderBottom: `1px solid ${contrastColor}`,
        color: contrastColor,
        height: '100%',
      }}
      justifyContent="space-between"
      alignItems="center"
    >
      <Link
        sx={{ textDecoration: 'none', color: 'inherit' }}
        href={generatePath(routes.locationHome.path, { accountName: locationName })}
      >
        <Typography variant="h5" color={contrastColor}>
          <strong>{location?.displayName}</strong>
        </Typography>
      </Link>
      <Stack direction="row" spacing={3} alignItems="center">
        {!isBelowMD && (
          <Stack
            direction="row"
            spacing={2}
            justifyContent={{ xs: 'left', md: 'center' }}
            sx={{ color: contrastColor, marginLeft: 2, '& a': { textDecorationColor: 'transparent' } }}
          >
            <Website responsive />
            <AddressLocation responsive />
            <Phone responsive />
          </Stack>
        )}
        {isLoggedIn ? (
          <>
            <AccountButton />
          </>
        ) : (
          location && (
            <SignInTooltip
              onClose={handleLoginTooltipClose}
              onError={handleLoginTooltipError}
              open={loginTooltipOpen}
              TagManager={TagManager}
            >
              <Button
                sx={{
                  color: contrastColor,
                  borderColor: contrastColor,
                  '&:hover': { borderColor: contrastColor },
                }}
                variant="outlined"
                onClick={handleLoginTooltipOpen}
              >
                {t('global.label.login')}
              </Button>
            </SignInTooltip>
          )
        )}
      </Stack>
    </Stack>
  )
}
